/* eslint-disable no-undef */
import { datadogRum, RumInitConfiguration } from "@datadog/browser-rum";
import { User } from "./types";
// version.js is overwritten by CI/CD with the actual commit SHA before build
import { version } from "./version";

declare const process: { env: Record<string, string | undefined> };

const ddEnvironments: string[] = ["qual", "cert"];

// Set once RUM is actually initialized so reporters below stay no-ops locally.
let rumEnabled = false;

export const startRum = (user: User): void => {
  const env = process.env.REACT_APP_ENV || "";

  if (!ddEnvironments.includes(env)) {
    return;
  }

  const options: RumInitConfiguration = {
    applicationId: "37a6d2c3-5eba-465c-8bf6-be2db2c5b1ee",
    clientToken: "pub56d19727de10baefb2da0a252b11888e",
    site: "datadoghq.com",
    service: "dune-orchestrator",
    env,
    version: version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    allowedTracingUrls: [
      /^https:\/\/[a-zA-Z0-9.-]*\.deere\.com/,
      /^https:\/\/[a-zA-Z0-9.-]*\.johndeerecloud\.com/,
    ],
  };

  datadogRum.init(options);
  rumEnabled = true;

  datadogRum.setUser({
    id: user.userId,
    name: user.name,
  });

  datadogRum.setGlobalContextProperty("jd_component", "cfard-dune-digital");
};

export const setOrgContext = (orgId: string): void => {
  datadogRum.setGlobalContextProperty("orgId", orgId);
};

// Network Information API (Chromium-only; undefined on Firefox/Safari). Gives a
// snapshot of the connection quality at the moment of the ping.
const getNetworkInfo = (): Record<string, unknown> => {
  const conn = (navigator as any)?.connection;
  if (!conn) {
    return { net_supported: false };
  }
  return {
    net_supported: true,
    net_effective_type: conn.effectiveType, // "4g" | "3g" | "2g" | "slow-2g"
    net_downlink_mbps: conn.downlink, // estimated bandwidth
    net_rtt_ms: conn.rtt, // estimated round-trip time
    net_save_data: conn.saveData, // data-saver on
  };
};

// browser_sent_at = browser clock when ping was sent; browser_received_at =
// browser clock when pong returned; server_received_at/server_sent_at = server
// clock when ping arrived and pong left (different machine, so cross-side diffs
// include clock skew, not pure network time).
export const reportSocketPing = (
  sentAt: number,
  receivedAt: number,
  serverReceivedAt?: number,
  serverSentAt?: number,
): void => {
  if (!rumEnabled) {
    return;
  }
  datadogRum.addAction("socket_ping_latency", {
    browser_sent_at: sentAt,
    browser_received_at: receivedAt,
    server_received_at: serverReceivedAt,
    server_sent_at: serverSentAt,
    latency_ms: receivedAt - sentAt,
    ...getNetworkInfo(),
  });
};

export const reportHighLatencyGoogleProbe = (
  socketLatencyMs: number,
  googleReachable: boolean,
  googleLatencyMs: number | null,
): void => {
  if (!rumEnabled) {
    return;
  }
  datadogRum.addAction("high_latency_google_probe", {
    socket_latency_ms: socketLatencyMs,
    google_reachable: googleReachable,
    google_latency_ms: googleLatencyMs,
    ...getNetworkInfo(),
  });
};
